import { createFileRoute, useLocation } from '@tanstack/react-router';
import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  resendManagerVerificationEmailDocument,
  verifyManagerEmailDocument,
} from '@social-garden/api/documents/manager.ts';
import {
  VerifyEmailForm,
  VerifyEmailSchema,
} from '@/constants/ValidationSchema.ts';
import CodeField from '@/components/CodeField.tsx';
import ReadOnlyEmailField from '@/components/ReadOnlyEmailField.tsx';
import useLoginManager from '@/hooks/useLoginManager.ts';
import { z } from 'zod';
import usePreventLeavingUnsavedForm from '@/hooks/usePreventLeavingUnsavedForm.ts';
import PageTitle from '@/components/PageTitle.tsx';

interface VerifyEmailState {
  password?: string;
}

const VerifyEmailSearchSchema = z.object({
  email: z.string(),
  code: z.string().optional(),
});

export const Route = createFileRoute('/_guest/verify-email')({
  component: VerifyEmail,
  validateSearch: (search) => VerifyEmailSearchSchema.parse(search),
});

function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as VerifyEmailState;
  const searchParams = Route.useSearch();
  const { t } = useTranslation(['manager']);
  const [verifyManagerEmail, { loading: verifyManagerEmailLoading }] =
    useMutation(verifyManagerEmailDocument);
  const [
    resendManagerVerificationEmail,
    { loading: resendManagerVerificationEmailLoading },
  ] = useMutation(resendManagerVerificationEmailDocument);

  const [loginManager, { loading: loginManagerLoading }] = useLoginManager();

  const {
    control,
    formState: { dirtyFields, isValid, isSubmitting },
    handleSubmit,
  } = useForm<VerifyEmailForm>({
    resolver: zodResolver(VerifyEmailSchema),
    mode: 'all',
    defaultValues: {
      email: searchParams.email,
      code: searchParams.code,
    },
  });

  usePreventLeavingUnsavedForm({ dirtyFields, isSubmitting });

  const handleOnResendCode = useCallback(async () => {
    await resendManagerVerificationEmail({
      variables: {
        input: {
          email: searchParams.email,
        },
      },
    });
  }, [searchParams.email, resendManagerVerificationEmail]);

  const handleOnValid = useCallback(
    async ({ email, code }: VerifyEmailForm) => {
      const result = await verifyManagerEmail({
        variables: {
          input: {
            email,
            code,
          },
        },
      });
      if (!result.data) {
        return;
      }
      if (state.password) {
        await loginManager({
          email,
          password: state.password,
        });
      } else {
        await navigate({ to: '/login' });
      }
    },
    [verifyManagerEmail, state.password, loginManager, navigate],
  );

  return (
    <Stack spacing={4}>
      <Stack spacing={1} alignItems="center">
        <PageTitle variant="h4" textAlign="center">
          {t('verifyEmail.header')}
        </PageTitle>
      </Stack>
      <Stack
        spacing={2}
        component="form"
        noValidate
        onSubmit={handleSubmit(handleOnValid)}>
        <Controller<VerifyEmailForm, 'email'>
          name="email"
          control={control}
          render={ReadOnlyEmailField}
        />
        <Controller<VerifyEmailForm, 'code'>
          name="code"
          control={control}
          render={CodeField}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={
            !isValid || verifyManagerEmailLoading || loginManagerLoading
          }>
          {t('verifyEmail.submitButton')}
        </Button>
        <Button
          variant="outlined"
          disabled={resendManagerVerificationEmailLoading}
          onClick={handleOnResendCode}>
          {t('verifyEmail.resendCodeButton')}
        </Button>
      </Stack>
    </Stack>
  );
}
